import React, { FC, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { MessageDescriptor } from "@formatjs/intl/src/types";
import Popover from "antd/es/popover";
import { LabeledValue } from "antd/es/select";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { AvatarWithName, SelectField } from "@mapmycustomers/ui";

import { isCurrentUserOwner } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import UsersSelect from "../UsersSelect";

import styles from "./UserSelectFilter.module.scss";

export interface UserSelectFilterProps {
  className?: string;
  onChange: (userIds: User["id"][]) => void;
  title?: MessageDescriptor;
  value: null | User["id"][];
}

interface Props extends UserSelectFilterProps {
  isCurrentUserOwner: boolean;
  teams: Team[];
  users: User[];
}

const UserSelectFilter: FC<Props> = ({
  className,
  isCurrentUserOwner,
  onChange,
  teams,
  users,
  value,
}) => {
  const intl = useIntl();

  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);
  const handleHide = useCallback(() => setPopoverVisibility(false), []);

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      onChange(userIds);
      handleHide();
    },
    [handleHide, onChange]
  );

  const filteredUsers = useMemo(
    () => users.filter((user) => !isOwner(user) || isCurrentUserOwner),
    [isCurrentUserOwner, users]
  );

  const selectedUsers = useMemo(
    () => users.filter(({ id }) => value?.includes(id)),
    [users, value]
  );

  const options = useMemo<LabeledValue[]>(
    () =>
      (selectedUsers ?? []).map((user) => ({
        key: String(user.id),
        label: <AvatarWithName size="xs" user={user} />,
        value: user.id,
      })) ?? [],
    [selectedUsers]
  );

  return (
    <Popover
      content={
        <UsersSelect
          allowEmpty
          allUsersWhenEmpty={false}
          onApply={handleApply}
          onCancel={handleHide}
          selectedUserIds={value ?? undefined}
          teams={teams}
          users={filteredUsers}
        />
      }
      destroyTooltipOnHide
      onOpenChange={setPopoverVisibility}
      open={popoverVisible}
      overlayClassName={styles.container}
      placement="left"
      trigger={["click"]}
    >
      <SelectField<User["id"][]>
        className={className}
        maxTagCount="responsive"
        mode="multiple"
        onChange={onChange}
        open={false}
        options={options}
        placeholder={intl.formatMessage({
          id: "formFields.usersSelectFilter.select",
          defaultMessage: "Select",
          description: "Placeholder of the Select Users",
        })}
        showArrow={false}
        size="middle"
        value={value}
      />
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  isCurrentUserOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(UserSelectFilter);
